import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@akina/infra";
import { useMaintenanceBypass, useMaintenanceStory } from "../hooks";
import { MAX_TIMEOUT_DELAY_MS, computeIsInMaintenanceRange, parseLocalDateTime } from "../utils/maintenance";
import { LoadingOverlay } from "./LoadingOverlay";
import { SideContentLayout } from "./SideContentLayout";
import { Button } from "./atoms/Button";
import { Headline } from "./atoms/Headline";
import { Text } from "./atoms/Text";

export function MaintenanceGuard({ children }: Readonly<{ children: ReactNode }>) {
  const { maintenance, isLoadingMaintenance, maintenanceCloseActions } = useMaintenanceStory();
  const [isCheckingForMaintenance, setIsCheckingForMaintenance] = useState(true);
  const [isInMaintenanceTimeRange, setIsInMaintenanceTimeRange] = useState(false);
  const [isMaintenancePastEndTimeState, setIsMaintenancePastEndTimeState] = useState(false);
  const queryClient = useQueryClient();
  const isBypassed = useMaintenanceBypass();
  const timeoutsRef = useRef<{ start?: number; end?: number }>({});

  useEffect(() => {
    if (timeoutsRef.current.start) globalThis.clearTimeout(timeoutsRef.current.start);
    if (timeoutsRef.current.end) globalThis.clearTimeout(timeoutsRef.current.end);
    timeoutsRef.current = {};

    try {
      if (isLoadingMaintenance) {
        return;
      }

      if (!maintenance?.ReleaseDate || !maintenance?.ReleaseEnd || maintenance.Deployed) {
        setIsInMaintenanceTimeRange(false);
        setIsCheckingForMaintenance(false);
        return;
      }

      const startDate = parseLocalDateTime(maintenance.ReleaseDate);
      const endDate = parseLocalDateTime(maintenance.ReleaseEnd);
      if (!startDate || !endDate) {
        setIsInMaintenanceTimeRange(false);
        setIsCheckingForMaintenance(false);
        return;
      }

      const now = new Date();
      const msUntilStart = startDate.getTime() - now.getTime();
      const msUntilEnd = endDate.getTime() - now.getTime();

      const recheck = () => {
        try {
          setIsInMaintenanceTimeRange(computeIsInMaintenanceRange(maintenance));
        } catch {
          setIsInMaintenanceTimeRange(false);
        }
      };

      const schedule = (cb: () => void, delayMs: number): number | undefined => {
        if (delayMs <= 0) {
          cb();
          return undefined;
        }
        const safeDelay = Math.min(delayMs, MAX_TIMEOUT_DELAY_MS);
        return globalThis.setTimeout(cb, safeDelay) as unknown as number;
      };

      if (now < startDate) {
        timeoutsRef.current.start = schedule(recheck, msUntilStart);
        timeoutsRef.current.end = schedule(recheck, msUntilEnd);
      } else if (now >= startDate && now < endDate) {
        timeoutsRef.current.end = schedule(recheck, msUntilEnd);
      }

      setIsInMaintenanceTimeRange(computeIsInMaintenanceRange(maintenance, now));
      setIsCheckingForMaintenance(false);

    } catch {
      setIsInMaintenanceTimeRange(false);
      setIsCheckingForMaintenance(false);
    }

    return () => {
      if (timeoutsRef.current.start) globalThis.clearTimeout(timeoutsRef.current.start);
      if (timeoutsRef.current.end) globalThis.clearTimeout(timeoutsRef.current.end);
    };
  }, [maintenance, isLoadingMaintenance]);

  useEffect(() => {
    if (!isInMaintenanceTimeRange || !maintenance?.ReleaseEnd) {
      setIsMaintenancePastEndTimeState(false);
      return;
    }

    const releaseEnd = maintenance.ReleaseEnd;

    function checkPastEndTime() {
      const endDate = parseLocalDateTime(releaseEnd);
      setIsMaintenancePastEndTimeState(endDate !== null && new Date() >= endDate);
    }

    checkPastEndTime();
    globalThis.addEventListener("visibilitychange", checkPastEndTime);
    globalThis.addEventListener("focus", checkPastEndTime);

    return () => {
      globalThis.removeEventListener("visibilitychange", checkPastEndTime);
      globalThis.removeEventListener("focus", checkPastEndTime);
    };
  }, [isInMaintenanceTimeRange, maintenance]);

  function handleForceCheck() {
    queryClient.invalidateQueries({ queryKey: [QueryKeys.fetchLocaleCopy] });
  }

  if (isBypassed) return <>{children}</>;

  if (isCheckingForMaintenance) {
    return <LoadingOverlay />;
  }

  if (isInMaintenanceTimeRange && maintenance?.ReleaseDate && maintenance?.ReleaseEnd) {
    return (
      <SideContentLayout>
        <div
          className="bg-primary-100 my-2 flex h-18 items-center gap-4 rounded-full px-7 py-4 select-none"
        >
          <div className="h-5 w-5 rounded-full bg-white" />
          <Text fontWeight="font-extrabold" className="mr-1 text-[20px] text-white">
            {maintenance.Tag}
          </Text>
        </div>
        <Headline fontWeight="font-bold" size="3xl">
          {maintenance.Title}
        </Headline>
        <Text className="text-lg leading-[32px] text-gray-800/70">{maintenance.Content}</Text>
        {isMaintenancePastEndTimeState ? (
          <>
            <Button
              variant="link"
              className="max-w-[200px]"
              loading={isLoadingMaintenance}
              disabled={isLoadingMaintenance}
              onClick={handleForceCheck}
            >
              {maintenanceCloseActions.title}
            </Button>
            <Text>{maintenanceCloseActions.subtitle}</Text>
          </>
        ) : null}
      </SideContentLayout>
    );
  }

  return <>{children}</>;
}